import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import { useDeviceInfo, window } from "@swa-ui/browser";
import { classNames } from "@swa-ui/string";

import { AriaLive } from "../AriaLive";
import { Button } from "../Button";
import { FadingDot } from "../FadingDot";
import { Icon } from "../Icon";
import styles from "./HorizontalGestureScroller.module.scss";

const HORIZONTAL_GESTURE_SCROLLER_FEEDBACK_DESIGN_TOKEN =
  "cmp-core-color-horizontal-gesture-scroller-feedback";
const HORIZONTAL_GESTURE_SCROLLER_ICON_COLOR_DESIGN_TOKEN =
  "cmp-core-color-horizontal-gesture-scroller-button-content";
const MINIMUM_MOVE_DISTANCE = 6;
const NEXT = "NEXT";
const PREVIOUS = "PREV";
const SCROLL_BUTTON_OFFSET = "-1rem";
const TRANSITION_DURATION_DOT = 300;

/**
 * HorizontalGestureScroller provides support to scroll left and right using touch and wheel gestures.
 * The actual scroll position is controlled by the calling component and will receive position updates
 * from the onMove callback.
 */

export const HorizontalGestureScroller = React.forwardRef((props, ref) => {
  const {
    alignment,
    children,
    className,
    hasButtons,
    hasButtonSpacing,
    moreContentIndicator,
    onClick,
    onFocus,
    onKeyDown,
    onMove,
    role,
    scrollByItem,
    scrollPosition,
    tabIndex,
    yArrowOffset,
  } = props;
  const { isTouchDevice } = useDeviceInfo();
  const container = ref || useRef();
  const contentRef = useRef();
  const nextButton = useRef();
  const prevButton = useRef();
  const [announceFocusChange, setAnnounceFocusChange] = useState(false);
  const [closestLeftChild, setClosestLeftChild] = useState(0);
  const [closestRightChild, setClosestRightChild] = useState(0);
  const [childWidths, setChildWidths] = useState([]);
  const [focusedButton, setFocusedButton] = useState();
  const [showLeftOverlay, setShowLeftOverlay] = useState(false);
  const [showRightOverlay, setShowRightOverlay] = useState(moreContentIndicator);

  useEffect(() => {
    if (contentRef.current) {
      const contentRect = contentRef.current.getBoundingClientRect();
      const wrapper = contentRef.current.firstElementChild;

      if (wrapper) {
        setChildWidths(
          Array.from(wrapper.children).map((child) => {
            const childRect = child.getBoundingClientRect();

            return {
              left: childRect.left - contentRect.left,
              width: child.offsetWidth,
            };
          })
        );
      }
    }
  }, [children]);

  useEffect(() => {
    if (scrollPosition && container.current) {
      container.current.scrollLeft = -scrollPosition;
    }
  }, [scrollPosition]);

  useEffect(() => {
    if (container.current) {
      container.current.addEventListener("scroll", handleOnMoveScroll);
    }

    return () => {
      if (container.current) {
        container.current.removeEventListener("scroll", handleOnMoveScroll);
      }
    };
  }, [onMove]);

  useEffect(() => {
    if (container.current) {
      container.current.addEventListener("scroll", handleScroll);
      handleScroll();
    }

    return () => {
      if (container.current) {
        container.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, [children, moreContentIndicator, scrollPosition]);

  return (
    <div {...getProps()}>
      {renderButtons()}
      <div {...getChildrenContainerProps()}>
        <div
          className={classNames(
            styles.horizontalGestureScrollerContent,
            alignment === "center" && styles.horizontalGestureScrollerContentCentered,
            alignment === "right" && styles.horizontalGestureScrollerAlignedRight
          )}
          ref={contentRef}
        >
          {children}
        </div>
      </div>
      {renderFocusChangeAriaLabel()}
    </div>
  );

  function renderButtons() {
    return (
      buttonsVisible(hasButtons) && (
        <>
          {renderButton(PREVIOUS, showLeftOverlay)}
          {renderButton(NEXT, showRightOverlay)}
        </>
      )
    );
  }

  function renderButton(direction, isVisible) {
    return (
      isVisible && (
        <Button {...getButtonProps(direction)}>
          {focusedButton === direction && <FadingDot {...getDotProps()} />}
          <Icon {...getIconProps(direction)} />
        </Button>
      )
    );
  }

  function renderFocusChangeAriaLabel() {
    const shouldAnnounceFocusChange = announceFocusChange && focusedButton;
    const focusChangeAnnouncement = shouldAnnounceFocusChange
      ? focusedButton === PREVIOUS
        ? props["aria-label-focus-change-previous"]
        : props["aria-label-focus-change-next"]
      : "";

    return <AriaLive>{focusChangeAnnouncement}</AriaLive>;
  }

  function getProps() {
    return {
      className: classNames(className, styles.horizontalGestureScrollerContainer),
      onFocus,
      onKeyDown,
    };
  }

  function getChildrenContainerProps() {
    return {
      className: classNames(
        styles.horizontalGestureScroller,
        buttonsVisible(hasButtons) &&
          hasButtonSpacing &&
          styles.horizontalGestureScrollerHasButtons,
        moreContentIndicator && {
          [styles.moreContentBoth]: showLeftOverlay && showRightOverlay,
          [styles.moreContentLeft]: showLeftOverlay && !showRightOverlay,
          [styles.moreContentRight]: showRightOverlay && !showLeftOverlay,
        }
      ),

      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      ref: container,
      role,
      tabIndex: tabIndex,
    };
  }

  function getButtonProps(direction) {
    const arrowOffsetStyle = {
      left: direction === PREVIOUS && !hasButtonSpacing ? SCROLL_BUTTON_OFFSET : undefined,
      right: direction === NEXT && !hasButtonSpacing ? SCROLL_BUTTON_OFFSET : undefined,
      top: yArrowOffset !== undefined ? `${yArrowOffset}px` : undefined,
    };

    return {
      "aria-label": props[direction === PREVIOUS ? "aria-label-previous" : "aria-label-next"],
      className: classNames({
        [styles.horizontalGestureScrollerButtonLeft]: direction === PREVIOUS,
        [styles.horizontalGestureScrollerButtonRight]: direction === NEXT,
      }),
      onBlur: () => setFocusedButton(direction),
      onClick: () => handleClick(direction),
      onFocus: () => setFocusedButton(direction),
      ref: direction === PREVIOUS ? prevButton : nextButton,
      style: arrowOffsetStyle,
    };
  }

  function getIconProps(direction) {
    const rotation = [direction === PREVIOUS ? "rotate270" : "rotate90"];

    return {
      actions: rotation,
      color: HORIZONTAL_GESTURE_SCROLLER_ICON_COLOR_DESIGN_TOKEN,
      initial: rotation,
      name: "ArrowThin",
      size: "size20",
      transparentBorder: true,
    };
  }

  function getDotProps() {
    return {
      className: styles.dot,
      color: HORIZONTAL_GESTURE_SCROLLER_FEEDBACK_DESIGN_TOKEN,
      duration: TRANSITION_DURATION_DOT,
      fullWidth: true,
      onTransformationEnd: handleTransformationEnd,
      to: "FADED-GROW",
    };
  }

  function handleClick(direction) {
    if (direction === PREVIOUS) {
      scrollLeftClick();
    } else {
      scrollRightClick();
    }

    if (focusedButton === direction) {
      setAnnounceFocusChange(true);

      if (direction === PREVIOUS) {
        prevButton.current.focus();
      } else {
        nextButton.current.focus();
      }
    } else {
      setAnnounceFocusChange(false);
    }

    onClick?.(direction);
  }

  function handleOnMoveScroll() {
    if (container.current) {
      onMove(-container.current.scrollLeft);
    }
  }

  function handleScroll() {
    if (container.current && contentRef.current) {
      const containerRect = container.current.getBoundingClientRect();
      const { scrollLeft, scrollWidth, clientWidth } = container.current;
      const wrapper = contentRef.current.firstElementChild;

      if (wrapper) {
        const distances = Array.from(wrapper.children).map((child) => {
          const childRect = child.getBoundingClientRect();

          return {
            left: Math.abs(childRect.left - containerRect.left),
            right: Math.abs(childRect.right - containerRect.right),
          };
        });

        const closestLeftChildDistance = distances.reduce(
          (closestIndex, distance, index, array) =>
            distance.left < array[closestIndex].left ? index : closestIndex,
          0
        );

        const closestRightChildDistance = distances.reduce(
          (closestIndex, distance, index, array) =>
            distance.right < array[closestIndex].right ? index : closestIndex,
          0
        );

        setClosestLeftChild(closestLeftChildDistance);
        setClosestRightChild(closestRightChildDistance);
      }

      setShowLeftOverlay(scrollLeft > MINIMUM_MOVE_DISTANCE);
      setShowRightOverlay(scrollLeft + clientWidth + MINIMUM_MOVE_DISTANCE < scrollWidth);
    }
  }

  function handleMouseEnter() {
    window.document.body.classList.add(styles.preventGestureNavigation);
  }

  function handleMouseLeave() {
    window.document.body.classList.remove(styles.preventGestureNavigation);
  }

  function handleTransformationEnd() {
    setFocusedButton();
  }

  function scrollLeftClick() {
    if (container.current && contentRef.current) {
      const { clientWidth } = container.current;

      if (scrollByItem) {
        container.current.scrollBy({
          behavior: "smooth",
          left: -childWidths[closestLeftChild].width,
        });
      } else {
        if (childWidths[closestLeftChild].width === clientWidth) {
          container.current.scrollBy({
            behavior: "smooth",
            left: -childWidths[closestLeftChild].width,
          });
        } else {
          if (closestLeftChild === childWidths.length - 1) {
            container.current.scrollBy({
              behavior: "smooth",
              left: -childWidths[closestLeftChild].width,
            });
          } else {
            container.current.scrollTo({
              behavior: "smooth",
              left:
                childWidths[closestLeftChild].left +
                childWidths[closestLeftChild].width -
                clientWidth,
            });
          }
        }
      }
    }
  }

  function scrollRightClick() {
    if (container.current) {
      const { clientWidth } = container.current;

      if (scrollByItem) {
        container.current.scrollBy({
          behavior: "smooth",
          left: childWidths[closestRightChild].width,
        });
      } else {
        if (childWidths[closestLeftChild].width === clientWidth) {
          container.current.scrollBy({
            behavior: "smooth",
            left: childWidths[closestLeftChild].width,
          });
        } else {
          container.current.scrollTo({
            behavior: "smooth",
            left: childWidths[closestRightChild].left,
          });
        }
      }
    }
  }

  function buttonsVisible(prop) {
    if (prop === "no") {
      return false;
    } else if (prop === "yes") {
      return true;
    } else if (prop === "auto") {
      return !isTouchDevice;
    } else {
      return false;
    }
  }
});

HorizontalGestureScroller.displayName = "HorizontalGestureScroller";

export const horizontalGestureScrollerPropTypes = {
  /** Items will be left aligned by default but can be centered when set to 'center'. */
  alignment: PropTypes.oneOf(["center", "left", "right"]),

  /**
   * Content that will be included in the announcement when focus is moved to the next page button.
   */
  "aria-label-focus-change-next": PropTypes.string,

  /**
   * Content that will be included in the announcement when focus is moved to the previous page button.
   */
  "aria-label-focus-change-previous": PropTypes.string,

  /** aria-label text to provide accessibility description for next page button. */
  "aria-label-next": PropTypes.string.isRequired,

  /** aria-label text to provide accessibility description for previous page button. */
  "aria-label-previous": PropTypes.string.isRequired,

  /** Content to be rendered. */
  children: PropTypes.node,

  /**
   * Additional classes for positioning the component. Given classes may only position this component for layout
   * purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Denotes if the component should render buttons for scrolling. Options are 'auto' which will automatically adjust them based on if the device is a touch device or not. 'yes' which will always include them, and 'no' the default. */
  hasButtons: PropTypes.oneOf(["auto", "yes", "no"]),

  /** Denotes if buttons are provided space to not overlay the content */
  hasButtonSpacing: PropTypes.bool,

  /** Denotes if a "faded edge" will be rendered to indicate more content is scrollable. */
  moreContentIndicator: PropTypes.bool,

  /** Callback that will be called when next or previous button is clicked. */
  onClick: PropTypes.func,

  /** Callback to learn when a child of HGS receives focus. */
  onFocus: PropTypes.func,

  /** Callback to learn when the HGS gets a key down event. */
  onKeyDown: PropTypes.func,

  /**
   * Callback to receive updated scroll position. The horizontal change will be passed as the only parameter to the
   * callback. The value means the container is being scrolled left.
   */
  onMove: PropTypes.func,

  /** Name used for aria role. */
  role: PropTypes.string,

  /** Denotes if the component should scroll by a single item or by an approximated visible length. */
  scrollByItem: PropTypes.bool,

  /** Scroll position. Values will mean the children content has scrolled to the left of the visible area. */
  scrollPosition: PropTypes.number,

  /** tabIndex for the scrollable area */
  tabIndex: PropTypes.number,

  /**
   * Allows the left and right buttons to be positioned manually from the top of the container.
   * if yArrowOffset is not given, the arrow buttons will be centered vertically.
   */
  yArrowOffset: PropTypes.number,
};

HorizontalGestureScroller.propTypes = horizontalGestureScrollerPropTypes;
HorizontalGestureScroller.defaultProps = {
  alignment: "left",
  hasButtons: "no",
  hasButtonSpacing: false,
  moreContentIndicator: true,
  onMove: () => {},
  scrollByItem: false,
  scrollPosition: 0,
  tabIndex: 0,
};
